import { CdnApi, FetchFromCdnOptions, AssetUrlResult } from './CdnApi'

/**
 * Base class for CDN API implementations with shared utility methods
 */
export abstract class BaseCdnApi implements CdnApi {
  private static readonly AVATAR_ASSETS_PREFIX = 'avatar3d'
  private static readonly SHARED_LIBS_PREFIX = 'libs'

  protected abstract getCdnAssetsBaseUrl(): string
  protected abstract getCdnBaseUrl(): string
  protected abstract getSharedAssetsCdnBaseUrl(): string

  /**
   * Remove leading slash from subPath
   */
  protected cleanSubPath(subPath: string): string {
    return subPath.startsWith('/') ? subPath.slice(1) : subPath
  }

  /**
   * Encode path parts - only encode the filename (last part) to preserve directory structure
   */
  protected encodePathParts(subPath: string): string {
    const pathParts = subPath.split('/')
    const encodedParts = pathParts.map((part, index) => {
      // Only encode the filename (last part), keep directory names as-is for URL structure
      return index === pathParts.length - 1 ? encodeURIComponent(part) : part
    })
    return encodedParts.join('/')
  }

  /**
   * Check if subPath is already a full URL
   */
  protected isFullUrl(subPath: string): boolean {
    return subPath.startsWith('http://') || subPath.startsWith('https://')
  }

  /**
   * Normalize base URL to not end with trailing slash
   */
  protected normalizeBaseUrl(url: string): string {
    return url.endsWith('/') ? url.slice(0, -1) : url
  }

  /**
   * Build full URL with encoding
   */
  protected buildCdnUrl(subPath: string, baseUrl: string): string {
    const cleanSubPath = this.cleanSubPath(subPath)
    const encodedSubPath = this.encodePathParts(cleanSubPath)
    return `${baseUrl}/${encodedSubPath}`
  }

  /**
   * Execute a fetch request with timeout handling
   */
  protected async executeFetch(url: string, options?: FetchFromCdnOptions): Promise<Blob> {
    const controller = new AbortController()
    const timeoutMs = options?.timeout ?? 30000
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit',
        headers: { Accept: '*/*' },
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      return await response.blob()
    } catch (error) {
      clearTimeout(timeoutId)
      throw error
    }
  }

  getAssetCdnBaseUrl(): string {
    return this.getCdnAssetsBaseUrl()
  }

  async resolveAssetUrl(subPath: string): Promise<string> {
    return this.buildCdnUrl(subPath, this.getCdnAssetsBaseUrl())
  }

  resolveAvatarAssetUrl(subPath: string): string {
    if (this.isFullUrl(subPath)) {
      return subPath
    }
    // Avatar assets use shared CDN base URL with avatar3d prefix
    return this.buildCdnUrl(`${BaseCdnApi.AVATAR_ASSETS_PREFIX}/${subPath}`, this.getSharedAssetsCdnBaseUrl())
  }

  resolveSharedLibUrl(subPath: string): string {
    if (this.isFullUrl(subPath)) {
      return subPath
    }
    // Shared libs use shared CDN base URL with libs prefix
    return this.buildCdnUrl(`${BaseCdnApi.SHARED_LIBS_PREFIX}/${subPath}`, this.getSharedAssetsCdnBaseUrl())
  }

  async resolveAssetUrls(subPaths: string[]): Promise<AssetUrlResult[]> {
    const results = await Promise.all(
      subPaths.map(async (p) => {
        try {
          const url = await this.resolveAssetUrl(p)
          return { path: p, status: 'ok' as const, url }
        } catch {
          return { path: p, status: 'error' as const, error: 'ASSET_NOT_FOUND' }
        }
      })
    )
    return results
  }

  async refreshEntitlements(): Promise<void> {
    // No-op in base — overridden by HostCdnApi
  }

  abstract fetchAsset(relativePath: string, options?: FetchFromCdnOptions): Promise<Blob>
  abstract fetchFromCdn(subPath: string, options?: FetchFromCdnOptions): Promise<Blob>
}
